import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import iziToast from 'izitoast';

import { addStudentToGroup, getGroupCandidates } from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import useIsMobile, { mobileDialogProps } from '../../utils/useIsMobile';
import { studentName, todayString } from '../../utils/journalFormat';

const DEBOUNCE_MS = 300;

/**
 * Добавление слушателя в группу.
 *
 * Список приходит с сервера уже отобранным: не отчислен, программа обучения подходит
 * под год группы или не заведена, не числится в другой группе того же типа. Раньше
 * в браузер выгружался весь контингент и фильтровался на месте — на проде это 3800
 * человек, и выпадающий список переставал успевать за вводом.
 */
const AddStudentModal = ({ open, groupId, onClose, onAdded }) => {
  const isMobile = useIsMobile();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Текст в поле и строка запроса разведены намеренно. После выбора в поле должно
  // оказаться полное имя, но перезапрашивать сервер из-за этого не нужно: запрос
  // меняется только когда печатают.
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [student, setStudent] = useState(null);
  const [dateFrom, setDateFrom] = useState(todayString());
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  // Ответ на устаревший запрос не должен затирать свежий: печатают быстрее, чем отвечает сеть.
  const requestId = useRef(0);

  useEffect(() => {
    if (open) {
      setStudent(null);
      setSearch('');
      setQuery('');
      setDateFrom(todayString());
      setComment('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const id = requestId.current + 1;
    requestId.current = id;

    setLoading(true);

    const timer = setTimeout(() => {
      getGroupCandidates(groupId, query)
        .then((items) => {
          if (requestId.current !== id) return;
          setOptions(items.map((item) => ({
            id: item.id,
            label: studentName(item),
            country: item.country || '',
            hours_number: item.hours_number || '',
          })));
        })
        .catch(() => {
          if (requestId.current === id) setOptions([]);
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [open, groupId, query]);

  const submit = () => {
    setSaving(true);
    addStudentToGroup(groupId, {
      student_id: student.id,
      date_from: dateFrom,
      comment: comment || undefined,
    })
      .then(() => {
        iziToast.success({ message: 'Слушатель добавлен в группу', position: 'topRight' });
        onAdded();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...mobileDialogProps(isMobile)}>
      <DialogTitle>Добавить слушателя в группу</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={options}
            loading={loading}
            value={student}
            onChange={(event, value) => setStudent(value)}
            inputValue={search}
            onInputChange={(event, value, reason) => {
              setSearch(value);
              // reason 'reset' — это подстановка выбранного имени, а не ввод.
              if (reason === 'input') setQuery(value);
            }}
            // Фильтрует сервер: повторная фильтрация в браузере спрятала бы найденное.
            filterOptions={(items) => items}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={query.length > 0 ? 'Никого не нашлось' : 'Подходящих слушателей нет'}
            loadingText="Ищем…"
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <span>
                  {option.label}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {[option.country, option.hours_number].filter(Boolean).join(' · ')}
                  </Typography>
                </span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Слушатель"
                helperText="Начните вводить фамилию"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="В группе с" type="date" value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Комментарий" value={comment} multiline rows={2}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Например: учился в СПб полгода"
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !student}>
          Добавить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStudentModal;
